import { useState } from 'react'
import { api } from '../api'
import { useAsync } from '../hooks'
import { AssetLibrary, LibraryAssetDetail } from '../components/AssetLibrary'
import { Notice, WorkspaceLoading } from '../components/ui'
import { UndoToast } from '../components/Deletion'
import type { LibraryAsset } from '../types'

export function AssetLibraryView({ onClip }: { onClip: (id: string) => void }) {
  const library = useAsync(api.library, [], { pollMs: 3000 })
  const [selected, setSelected] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [removed, setRemoved] = useState<LibraryAsset | null>(null)
  const [failure, setFailure] = useState('')
  const detail = library.data?.find(asset => asset.id === selected && asset.origin === 'external')
  function update(asset: LibraryAsset) { library.setData(items => items?.map(item => item.id === asset.id ? asset : item) || [asset]) }
  async function move(id: string, day: string | null) {
    const asset = library.data?.find(item => item.id === id)
    if (!asset) return
    update(await api.libraryEditorial(id, { revision: asset.editorial_plan.revision, planned_day: day, position: null }))
  }
  async function remove(asset: LibraryAsset) {
    if (deletingId) return
    setDeletingId(asset.id); setFailure('')
    try {
      await api.deleteLibraryAsset(asset.id)
      library.setData(items => items?.filter(item => item.id !== asset.id) || [])
      setSelected(current => current === asset.id ? null : current)
      setRemoved(asset)
      void library.refresh()
    } catch (error) { setFailure(error instanceof Error ? error.message : 'The asset could not be deleted.') }
    finally { setDeletingId(null) }
  }
  async function undo() {
    if (!removed || deletingId) return
    const asset = removed
    setDeletingId(asset.id); setFailure('')
    try {
      await api.restoreLibraryAsset(asset.id)
      setRemoved(null)
      await library.refresh()
    } catch (error) { setFailure(error instanceof Error ? error.message : 'The asset could not be restored.') }
    finally { setDeletingId(null) }
  }
  if (library.data === null && !library.error) return <WorkspaceLoading label="Loading asset library" />
  return <div className="page-content asset-library-page">
    {failure ? <Notice tone="error" onDismiss={() => setFailure('')}>{failure}</Notice> : null}
    <AssetLibrary assets={library.data || []} error={library.error || ''} onOpen={asset => asset.origin === 'motion' && asset.clip_id ? onClip(asset.clip_id) : setSelected(asset.id)} onRefresh={library.refresh} onDelete={asset => void remove(asset)} deletingId={deletingId} />
    {detail ? <LibraryAssetDetail key={detail.id} asset={detail} onClose={() => setSelected(null)} onUpdate={update} onMove={move} /> : null}
    {removed ? <UndoToast key={removed.id} message={`${removed.title} deleted.`} onUndo={() => void undo()} onDismiss={() => setRemoved(null)} busy={deletingId === removed.id} /> : null}
  </div>
}
